import React, { useEffect, useState } from 'react';
import { Language, Theme } from '../types';
import { supabase } from '../services/supabaseClient';

interface BillingScreenProps {
  theme: Theme;
  language: Language;
  t: any;
  onBack: () => void;
  onGoToPlan?: () => void;
}

type BillingPayload = {
  ok?: boolean;
  entitlements?: {
    plan?: number | null;
    credits_remaining?: number | null;
    credits_reset_at?: string | null;
    plan_expires_at?: string | null;
  } | null;
  customer?: {
    email?: string | null;
    name?: string | null;
    phone?: string | null;
  } | null;
  payment_method?: {
    brand?: string | null;
    last4?: string | null;
  } | null;
  invoices?: {
    data: Array<{
      id: string;
      number: string | null;
      status: string | null;
      amount_due: number | null;
      amount_paid: number | null;
      currency: string | null;
      created: number | null;
      hosted_invoice_url: string | null;
      invoice_pdf: string | null;
    }>;
    has_more: boolean;
    next_cursor: string | null;
    prev_cursor: string | null;
  } | null;
};

const planLabel = (plan: number | null | undefined, t: any) => {
  if (plan === 2) return t.pricingStarterName;
  if (plan === 3) return t.pricingProName;
  if (plan === 4) return t.pricingEnterpriseName;
  return t.freePlanName;
};

export const BillingScreen: React.FC<BillingScreenProps> = ({ theme, language, t, onBack, onGoToPlan }) => {
  const [loading, setLoading] = useState(true);
  const [entitlements, setEntitlements] = useState<BillingPayload['entitlements']>(null);
  const [customer, setCustomer] = useState<BillingPayload['customer']>(null);
  const [paymentMethod, setPaymentMethod] = useState<BillingPayload['payment_method']>(null);
  const [invoices, setInvoices] = useState<BillingPayload['invoices']>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const isDark = theme === 'dark';
  const pageSize = 10;
  const skeletonBlock = isDark ? 'bg-slate-800/70' : 'bg-slate-200';
  const handleGoToPlan = () => {
    if (onGoToPlan) {
      onGoToPlan();
      return;
    }
    window.location.assign('/app/plan');
  };

  const getAccessToken = async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    let token = sessionData.session?.access_token ?? null;
    if (!token) {
      const { data: refreshed } = await supabase.auth.refreshSession();
      token = refreshed.session?.access_token ?? null;
    }
    return token;
  };

  const loadBilling = async (options?: { starting_after?: string | null; ending_before?: string | null; nextIndex?: number }) => {
    setLoading(true);
    setError(null);

    const accessToken = await getAccessToken();
    if (!accessToken) {
      setError(t.billingNeedAuth);
      setHasLoaded(true);
      setLoading(false);
      return;
    }

    const body: Record<string, any> = { limit: pageSize };
    if (options?.starting_after) body.starting_after = options.starting_after;
    if (options?.ending_before) body.ending_before = options.ending_before;

    const { data, error: fnError } = await supabase.functions.invoke('billing-info', {
      body,
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (fnError || !data?.ok) {
      setError(t.billingLoadError);
      setHasLoaded(true);
      setLoading(false);
      return;
    }

    const payload = data as BillingPayload;
    setEntitlements(payload.entitlements ?? null);
    setCustomer(payload.customer ?? null);
    setPaymentMethod(payload.payment_method ?? null);
    setInvoices(payload.invoices ?? null);
    setPageIndex(options?.nextIndex ?? 0);
    setHasLoaded(true);
    setLoading(false);
  };

  useEffect(() => {
    loadBilling();
  }, []);

  const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US') : '—';
  const formatDateFromUnix = (value?: number | null) =>
    value ? new Date(value * 1000).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US') : '—';
  const formatAmount = (amount?: number | null, currency?: string | null) => {
    if (amount === null || amount === undefined || !currency) return '—';
    const value = amount / 100;
    try {
      return new Intl.NumberFormat(language === 'es' ? 'es-ES' : 'en-US', {
        style: 'currency',
        currency: currency.toUpperCase(),
      }).format(value);
    } catch {
      return `${value.toFixed(2)} ${currency.toUpperCase()}`;
    }
  };
  const paymentLabel = paymentMethod?.brand && paymentMethod?.last4
    ? `${paymentMethod.brand.toUpperCase()} •••• ${paymentMethod.last4}`
    : '—';
  const showSkeleton = loading && !hasLoaded;

  return (
    <div className="max-w-5xl mx-auto px-4 pb-8">
      <div className="flex items-center justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={onBack}
            className={`inline-flex items-center gap-2 text-xs uppercase tracking-widest ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
          >
            <span aria-hidden="true">←</span>
            {t.accountBack}
          </button>
          <h1 className={`mt-3 text-2xl font-semibold sm:text-3xl ${isDark ? 'text-white' : 'text-slate-900'}`}>{t.billingTitle}</h1>
        </div>
      </div>

      <div className="mt-10 grid gap-6">
        {showSkeleton ? (
          <>
            <section className={`rounded-3xl border p-4 sm:p-6 ${isDark ? 'border-white/10 bg-slate-900/60' : 'border-slate-200 bg-white'}`}>
              <div className={`h-5 w-28 rounded-full ${skeletonBlock} animate-pulse`} />
              <div className="mt-4 grid gap-4 md:grid-cols-3">
                <div className={`h-20 rounded-2xl ${skeletonBlock} animate-pulse`} />
                <div className={`h-20 rounded-2xl ${skeletonBlock} animate-pulse`} />
                <div className={`h-20 rounded-2xl ${skeletonBlock} animate-pulse`} />
              </div>
            </section>

            <section className={`rounded-3xl border p-4 sm:p-6 ${isDark ? 'border-white/10 bg-slate-900/60' : 'border-slate-200 bg-white'}`}>
              <div className={`h-5 w-44 rounded-full ${skeletonBlock} animate-pulse`} />
              <div className="mt-4 grid gap-4 md:grid-cols-3">
                <div className={`h-20 rounded-2xl ${skeletonBlock} animate-pulse`} />
                <div className={`h-20 rounded-2xl ${skeletonBlock} animate-pulse`} />
                <div className={`h-20 rounded-2xl ${skeletonBlock} animate-pulse`} />
              </div>
              <div className="mt-6">
                <div className={`h-16 rounded-2xl ${skeletonBlock} animate-pulse`} />
              </div>
            </section>

            <section className={`rounded-3xl border p-4 sm:p-6 ${isDark ? 'border-white/10 bg-slate-900/60' : 'border-slate-200 bg-white'}`}>
              <div className="flex items-center justify-between gap-4">
                <div className={`h-5 w-28 rounded-full ${skeletonBlock} animate-pulse`} />
                <div className="flex items-center gap-2">
                  <div className={`h-7 w-20 rounded-full ${skeletonBlock} animate-pulse`} />
                  <div className={`h-7 w-20 rounded-full ${skeletonBlock} animate-pulse`} />
                </div>
              </div>
              <div className="mt-4 space-y-3">
                <div className={`h-16 rounded-2xl ${skeletonBlock} animate-pulse`} />
                <div className={`h-16 rounded-2xl ${skeletonBlock} animate-pulse`} />
                <div className={`h-16 rounded-2xl ${skeletonBlock} animate-pulse`} />
              </div>
            </section>
          </>
        ) : (
          <>
            <section className={`rounded-3xl border p-4 sm:p-6 ${isDark ? 'border-white/10 bg-slate-900/60' : 'border-slate-200 bg-white'}`}>
              <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{t.billingCurrentPlan}</h2>
              <div className="mt-4 grid gap-4 md:grid-cols-3">
                <button
                  type="button"
                  onClick={handleGoToPlan}
                  className={`rounded-2xl border px-4 py-3 sm:py-4 text-left transition ${
                    isDark ? 'border-white/10 bg-slate-950 hover:bg-slate-900' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
                  }`}
                >
                  <p className="text-xs uppercase tracking-widest text-slate-500">{t.planTypeLabel}</p>
                  <p className={`mt-2 text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{planLabel(entitlements?.plan, t)}</p>
                </button>
                <button
                  type="button"
                  onClick={handleGoToPlan}
                  className={`rounded-2xl border px-4 py-3 sm:py-4 text-left transition ${
                    isDark ? 'border-white/10 bg-slate-950 hover:bg-slate-900' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
                  }`}
                >
                  <p className="text-xs uppercase tracking-widest text-slate-500">{t.creditsLabel}</p>
                  <p className={`mt-2 text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {entitlements?.credits_remaining ?? '—'}
                  </p>
                </button>
                <div className={`rounded-2xl border px-4 py-3 sm:py-4 ${isDark ? 'border-white/10 bg-slate-950' : 'border-slate-200 bg-slate-50'}`}>
                  <p className="text-xs uppercase tracking-widest text-slate-500">{t.creditsRenewalLabel}</p>
                  <p className={`mt-2 text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {formatDate(entitlements?.credits_reset_at ?? entitlements?.plan_expires_at)}
                  </p>
                </div>
              </div>
            </section>

            <section className={`rounded-3xl border p-4 sm:p-6 ${isDark ? 'border-white/10 bg-slate-900/60' : 'border-slate-200 bg-white'}`}>
              {(!customer?.email && !customer?.name && !customer?.phone && paymentLabel === '—') ? (
                <div className="flex flex-col items-center justify-center text-center py-10">
                  <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    {t.billingNoActivePlan}
                  </p>
                  <button
                    type="button"
                    onClick={handleGoToPlan}
                    className="mt-4 rounded-full bg-[#038759] px-5 py-2 text-xs font-semibold text-white transition hover:bg-[#026e49]"
                  >
                    {t.billingGoToPlan}
                  </button>
                </div>
              ) : (
                <>
                  <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{t.billingProfile}</h2>
                  <div className="mt-4 grid gap-4 md:grid-cols-3">
                    <div className={`rounded-2xl border px-4 py-3 sm:py-4 ${isDark ? 'border-white/10 bg-slate-950' : 'border-slate-200 bg-slate-50'}`}>
                      <p className="text-xs uppercase tracking-widest text-slate-500">{t.billingEmail}</p>
                      <p className={`mt-2 text-sm font-semibold break-words ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {customer?.email ?? '—'}
                      </p>
                    </div>
                    <div className={`rounded-2xl border px-4 py-3 sm:py-4 ${isDark ? 'border-white/10 bg-slate-950' : 'border-slate-200 bg-slate-50'}`}>
                      <p className="text-xs uppercase tracking-widest text-slate-500">{t.billingName}</p>
                      <p className={`mt-2 text-sm font-semibold break-words ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {customer?.name ?? '—'}
                      </p>
                    </div>
                    <div className={`rounded-2xl border px-4 py-3 sm:py-4 ${isDark ? 'border-white/10 bg-slate-950' : 'border-slate-200 bg-slate-50'}`}>
                      <p className="text-xs uppercase tracking-widest text-slate-500">{t.billingPhone}</p>
                      <p className={`mt-2 text-sm font-semibold break-words ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {customer?.phone ?? '—'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-6">
                    <div className={`rounded-2xl border px-4 py-3 sm:py-4 ${isDark ? 'border-white/10 bg-slate-950' : 'border-slate-200 bg-slate-50'}`}>
                      <p className="text-xs uppercase tracking-widest text-slate-500">{t.billingPaymentMethod}</p>
                      <p className={`mt-2 text-sm font-semibold break-words ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {paymentLabel}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </section>

            <section className={`rounded-3xl border p-4 sm:p-6 ${isDark ? 'border-white/10 bg-slate-900/60' : 'border-slate-200 bg-white'}`}>
              <div className="flex items-center justify-between gap-4">
                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{t.billingInvoices}</h2>
              </div>
              <div className="mt-4 space-y-3">
                {invoices?.data?.length ? (
                  invoices.data.map((invoice) => {
                    const amount = invoice.amount_paid && invoice.amount_paid > 0
                      ? invoice.amount_paid
                      : invoice.amount_due;
                    const downloadUrl = invoice.invoice_pdf || invoice.hosted_invoice_url;
                    return (
                      <div
                        key={invoice.id}
                        className={`rounded-2xl border px-4 py-4 flex min-w-0 flex-col gap-2 md:flex-row md:items-center md:justify-between ${isDark ? 'border-white/10 bg-slate-950' : 'border-slate-200 bg-slate-50'}`}
                      >
                        <div className="min-w-0">
                          <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            <span className="block break-words">{invoice.number || t.billingInvoiceFallback}</span>
                          </p>
                          <p className="text-xs text-slate-500">
                            {formatDateFromUnix(invoice.created)} · {invoice.status || '—'}
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            {formatAmount(amount, invoice.currency)}
                          </p>
                          {downloadUrl ? (
                            <a
                              href={downloadUrl}
                              target="_blank"
                              rel="noreferrer"
                              className={`rounded-full px-3 py-1 text-xs font-semibold transition ${isDark ? 'bg-white text-slate-900 hover:bg-slate-200' : 'bg-slate-900 text-white hover:bg-slate-800'}`}
                            >
                              {t.billingDownload}
                            </a>
                          ) : (
                            <span className="text-xs text-slate-500">—</span>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-slate-500">{t.billingNoInvoices}</p>
                )}
              </div>
              {Boolean(invoices?.data?.length && (invoices.data.length >= pageSize || invoices?.has_more || pageIndex > 0)) && (
                <div className="mt-6 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    disabled={pageIndex === 0 || !invoices?.data?.length}
                    onClick={() => {
                      const firstId = invoices?.data?.[0]?.id;
                      if (firstId) {
                        loadBilling({ ending_before: firstId, nextIndex: Math.max(pageIndex - 1, 0) });
                      }
                    }}
                    className={`rounded-full px-3 py-1 text-[11px] font-semibold transition sm:text-xs ${
                      pageIndex === 0 || !invoices?.data?.length
                        ? 'cursor-not-allowed bg-slate-200 text-slate-500'
                        : isDark
                          ? 'border border-white/20 text-white/80 hover:bg-white/10'
                          : 'border border-slate-300 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {t.billingPrevious}
                  </button>
                  <button
                    type="button"
                    disabled={!invoices?.has_more}
                    onClick={() => {
                      const lastId = invoices?.data?.[invoices.data.length - 1]?.id;
                      if (lastId) {
                        loadBilling({ starting_after: lastId, nextIndex: pageIndex + 1 });
                      }
                    }}
                    className={`rounded-full px-3 py-1 text-[11px] font-semibold transition sm:text-xs ${
                      invoices?.has_more
                        ? isDark
                          ? 'bg-white text-slate-900 hover:bg-slate-200'
                          : 'bg-slate-900 text-white hover:bg-slate-800'
                        : 'cursor-not-allowed bg-slate-200 text-slate-500'
                    }`}
                  >
                    {t.billingNext}
                  </button>
                </div>
              )}
            </section>

            {error && <p className="text-xs text-rose-400">{error}</p>}
          </>
        )}
      </div>
    </div>
  );
};
